/* eslint-disable max-lines, class-methods-use-this */

import React, { Component, createRef } from 'react';
import cx from 'classnames';
import { debounce } from 'lodash';
import { generateId } from 'utils/string';
import {
    cloneElement,
    renderProp,
    omitProps,
    deriveStateFromWatchedProps,
    pickProps,
} from 'utils/react';
import { mergeRefs } from 'utilsTS/react';
import { eventHasKey, getKeyFromEvent, KEY } from 'utils/dom';
import { MenuDropdown, findItemByValue, flattenItems } from 'components/Dropdown';
import Selectable from '../Selectable';
import { propTypes, defaultProps } from './selectContainerPropTypes';
import SelectToggle from './SelectToggle';
import MultiSelectableItem from './MultiSelectableItem';
import styles from './SelectContainer.module.scss';

export default class SelectContainer extends Component {
    static displayName = 'SelectContainer';

    static propTypes = {
        ...propTypes,
    };

    static defaultProps = {
        ...defaultProps,
    };

    static getDerivedStateFromProps(props, state) {
        return deriveStateFromWatchedProps(props, state, {
            id: () => props.id || generateId('dropdown'),
            isOpen: () => props.isOpen,
        });
    }

    state = {
        id: null,
        isOpen: undefined,
        filter: '',
        selectedValues: [],
        selectedAll: false,
        highlightedItem: null,
    };

    popoverRef = createRef();

    dropdownRef = createRef();

    toggleRef = createRef();

    selectAllRef = createRef();

    constructor(props) {
        super(props);

        const id = generateId('selectAll');

        this.selectAllRef.current = {
            id,
            value: id,
            label: '',
            disabled: false,
        };
    }

    componentDidMount() {
        const { value, clearFilterTimeout } = this.props;

        this.clearFilter = debounce(this.clearFilter, clearFilterTimeout);

        this.setSelectedItemsFromValues(value);
    }

    componentDidUpdate(previousProps, previousState) {
        const { value } = this.props;

        const allItems = this.getAllItems();
        const previousItems = flattenItems(previousProps.items);

        const shouldReevaluate = allItems.length !== previousItems.length;
        const hasNewValue = value !== previousProps.value;

        if (shouldReevaluate || hasNewValue) {
            this.setSelectedItemsFromValues(value);
        }
    }

    getAllItems({ onlyValid = false } = {}) {
        const { items } = this.props;
        const allItems = flattenItems(items);

        if (!onlyValid) return allItems;
        return allItems.filter(this.isValidItem);
    }

    getItemsToRender() {
        const { items, filterItems, clearItemLabel, multiple } = this.props;
        const clearItem = { label: clearItemLabel, value: '' };
        const isClearing = clearItemLabel && !multiple;

        if (items.length === 0) return isClearing ? [clearItem] : [];

        let result = [];

        const simpleItems = this.getAllItems();
        const filteredSimpleItems = filterItems(simpleItems);
        const simpleItemsToRender = filteredSimpleItems || simpleItems;

        result = this.isSection(items[0])
            ? items.map((sectionItem) => {
                  const filteredSectionItems = sectionItem.items.filter((item) =>
                      simpleItemsToRender.includes(item),
                  );

                  return { ...sectionItem, items: filteredSectionItems };
              })
            : items.filter((item) => simpleItemsToRender.includes(item));

        return isClearing ? [clearItem, ...result] : result;
    }

    getFilteredItems() {
        const { filterItems } = this.props;
        const items = this.getAllItems();
        const itemsFiltered = filterItems(items);
        return itemsFiltered || items;
    }

    getLastSelectedItem() {
        const selectedItems = this.getSelectedItems();

        if (selectedItems.length === 0) return null;

        return selectedItems[selectedItems.length - 1];
    }

    // eslint-disable-next-line max-statements
    getNextItem({ reverse = false, first = false, last = false, filter = null }) {
        const { isOpen } = this.state;
        const { highlightedItem } = this.state;

        const itemsOriginal = this.getFilteredItems();
        const itemsDir = reverse ? [...itemsOriginal].reverse() : itemsOriginal;
        const itemsFiltered = itemsDir
            .filter(this.isValidItem)
            .filter((item) => (filter ? filter(item) : true));

        // double items to start from beginning if near end
        const items = [...itemsFiltered, ...itemsFiltered];

        if (first) {
            const [firstItem] = items;
            return firstItem;
        }

        if (last) {
            const [nextItem] = items.slice(-1);
            return nextItem;
        }

        const currentItem = isOpen ? highlightedItem : this.getLastSelectedItem();
        const nextIndex = items.indexOf(currentItem) + 1;

        const [nextItem] = items.slice(nextIndex);
        return nextItem;
    }

    getIndicesOfItems(items) {
        return items.map((item) => this.getIndexOfItem(item));
    }

    getIndexOfItem(item) {
        if (!item) {
            return null;
        }

        const originalArray = this.getAllItems();
        /* istanbul ignore if */
        if (!originalArray) return null;

        const index = originalArray.indexOf(item);
        return index >= 0 ? index : null;
    }

    getSelection() {
        const { multiple } = this.props;
        const selectedItems = this.getSelectedItems();

        if (multiple) {
            return selectedItems;
        }

        return selectedItems[0];
    }

    getSelectedItems() {
        const { selectedValues } = this.state;
        const items = this.getAllItems();

        const selectedItems = selectedValues
            .map((selectedValue) => findItemByValue(items, selectedValue))
            .filter(Boolean);

        return selectedItems;
    }

    getSelectedItemsWithToggledItem(toggledItem) {
        const { selectedValues } = this.state;

        return this.getAllItems().filter((item) => {
            const isSelected = selectedValues.includes(item.value);
            const isToggledItem = item === toggledItem;

            if (isSelected) return !isToggledItem;
            return isToggledItem;
        });
    }

    setFilter = (filter) => {
        this.setState({ filter: filter.toLowerCase() });
        this.clearFilter();
    };

    setSelectedItemsFromValues(value) {
        const values = Array.isArray(value) ? value : [value];
        const items = this.getAllItems();

        const selectedItems = values
            .map((selectedValue) => findItemByValue(items, selectedValue))
            .filter(Boolean);

        this.setSelectedItems(selectedItems, { dispatchEvents: false });
    }

    setSelectedItems(selectedItems, { dispatchEvents = true } = {}) {
        const { multiple, onBeforeSelect, onSelect } = this.props;

        if (!selectedItems) return;

        const isClearing = !multiple && selectedItems[0]?.value === '';

        const selectedIndices = multiple
            ? [selectedItems, this.getIndicesOfItems(selectedItems)]
            : [selectedItems[0], this.getIndexOfItem(selectedItems[0])];

        const eventArguments = isClearing ? [null] : selectedIndices;

        if (dispatchEvents && onBeforeSelect(...eventArguments) === false) return;

        if (isClearing) {
            this.resetSelection();
        } else {
            this.setState({
                selectedValues: selectedItems.map((item) => item.value),
                selectedAll: this.isAllSelected(selectedItems),
            });
        }

        if (dispatchEvents && onSelect) {
            onSelect(...eventArguments);
        }
    }

    isSelectAllAvailable() {
        const { multiple, allowSelectAll } = this.props;

        const allItems = this.getAllItems();
        const filteredItems = this.getFilteredItems();

        return multiple && allowSelectAll && filteredItems.length === allItems.length;
    }

    closeDropdown = () => {
        const { isOpen } = this.state;
        const { current: dropdown } = this.dropdownRef;
        const { current: toggle } = this.toggleRef;

        if (isOpen) {
            this.resetHighlightedItem();
            dropdown.close();
            toggle?.focus();
        }
    };

    // eslint-disable-next-line react/no-unused-class-component-methods
    openDropdown = () => {
        const { isOpen } = this.state;
        const { current: dropdown } = this.dropdownRef;

        if (!isOpen) {
            dropdown.open();
        }
    };

    itemToString = (item) => {
        const { itemToString } = this.props;
        return itemToString(item) || item.label.toString();
    };

    isValidItem = (item) => !item.disabled;

    isSection = (item) => !!item?.items;

    clearFilter = () => {
        this.setState({ filter: '' });
    };

    selectItem = (selectedItem) => {
        const { multiple } = this.props;

        if (!selectedItem) return;

        if (multiple) {
            if (this.isItemSelectAll(selectedItem)) {
                this.selectAll();
            } else {
                const selectedItems = this.getSelectedItemsWithToggledItem(selectedItem);
                this.setSelectedItems(selectedItems);
                this.highlightItem(selectedItem);
            }
        } else {
            this.setSelectedItems([selectedItem]);
            this.closeDropdown();
        }
    };

    selectAll = () => {
        const { allowSelectAll } = this.props;
        const { selectedAll } = this.state;

        if (!allowSelectAll) return;

        if (selectedAll) {
            this.setSelectedItems([]);
        } else {
            const validItems = this.getAllItems({ onlyValid: true });
            this.setSelectedItems(validItems);
        }
    };

    highlightItem = (highlightedItem) => {
        const { onHighlight } = this.props;

        this.setState({ highlightedItem });

        const isItem = !this.isSelectAllAvailable() || !this.isItemSelectAll(highlightedItem);

        if (onHighlight && isItem) {
            onHighlight(highlightedItem, this.getIndexOfItem(highlightedItem));
        }
    };

    resetHighlightedItem = () => {
        this.setState({ highlightedItem: null });
    };

    handleBeforeOpen = () => {
        const { onBeforeOpen } = this.props;

        const selectedItems = this.getSelectedItems();
        if (selectedItems.length > 0) {
            const isAllSelected = this.isSelectAllAvailable() && this.isAllSelected(selectedItems);
            const lastItem = selectedItems[selectedItems.length - 1];
            const item = isAllSelected ? this.selectAllRef.current : lastItem;
            this.highlightItem(item);
        }

        this.setState({ isOpen: true });
        onBeforeOpen();
    };

    handleBeforeClose = () => {
        const { onBeforeClose } = this.props;
        this.setState({ isOpen: false });
        onBeforeClose();
    };

    handleClickOutside = (event, isClick) => {
        const { target } = event;
        const { multiple } = this.props;
        const { isOpen, highlightedItem } = this.state;

        if (!isOpen) return;

        if (isClick) {
            if (this.popoverRef.current?.contains(target)) {
                return;
            }

            if (highlightedItem && !multiple) {
                this.selectItem(highlightedItem);
                // successfully selecting item also closes Dropdown
                return;
            }
        }

        this.closeDropdown();
    };

    handleKeyDown = (event) => {
        const { TAB, ENTER, SPACE, HOME, END, UP, DOWN } = KEY;
        const { onKeyDown } = this.props;
        const key = getKeyFromEvent(event);

        if (eventHasKey(event, HOME, END, UP, DOWN)) {
            this.handleKeyDownNav(event);
        } else if (eventHasKey(event, ENTER, SPACE)) {
            this.handleKeyDownEnter(event);
        } else if (eventHasKey(event, TAB)) {
            this.handleKeyDownTab(event);
        } else if (key && key.length === 1) {
            this.handleKeyDownChar(event);
        }

        onKeyDown(event);
    };

    handleKeyDownNav = (event) => {
        const { HOME, END, DOWN } = KEY;
        const { selectWithArrow, ignoreNavigationKeys, multiple } = this.props;
        const { isOpen } = this.state;
        const { current: dropdown } = this.dropdownRef;

        if (isOpen) return;
        if (eventHasKey(event, HOME, END) && ignoreNavigationKeys) return;

        if (multiple) {
            event.preventDefault();

            dropdown.open();
        } else {
            if (!selectWithArrow) return;

            event.preventDefault();

            const isDown = eventHasKey(event, END, DOWN);
            const isLast = eventHasKey(event, HOME, END);
            const nextItem = this.getNextItem({ reverse: !isDown, last: isLast });

            this.selectItem(nextItem);
        }
    };

    handleKeyDownEnter = (event) => {
        const { ENTER, SPACE } = KEY;
        const { multiple, toggleWithEnter, toggleWithSpace } = this.props;
        const { highlightedItem, isOpen } = this.state;
        const { current: dropdown } = this.dropdownRef;

        const isEnter = eventHasKey(event, ENTER);
        const isSpace = eventHasKey(event, SPACE);

        if (!(isEnter && toggleWithEnter) && !(isSpace && toggleWithSpace)) return;

        if (!isOpen || !highlightedItem || (!multiple && highlightedItem)) {
            event.preventDefault();
            dropdown.toggle();
        }
    };

    handleKeyDownTab = (event) => this.closeDropdown();

    handleKeyDownChar = (event) => {
        const { selectWithChar, highlightWithChar, multiple } = this.props;
        const { filter: filterPrevious, isOpen } = this.state;
        const { current: dropdown } = this.dropdownRef;

        if (!isOpen && !selectWithChar) return;
        if (isOpen && !highlightWithChar) return;

        const key = getKeyFromEvent(event);
        const filter = filterPrevious + key;
        this.setFilter(filter);

        const nextItem = this.getNextItem({
            first: true,
            filter: (item) => {
                const value = this.itemToString(item);
                return value.toLowerCase().startsWith(filter);
            },
        });

        if (nextItem) {
            if (isOpen) {
                this.highlightItem(nextItem);
            } else if (multiple) {
                dropdown.open();
            } else {
                this.selectItem(nextItem);
            }
        }
    };

    handleSelectableClick = (event, selectedItem) => {
        this.selectItem(selectedItem);
    };

    getItemProps = (item) => {
        const { multiple, selectAllText, checkboxClassName, selectAllContentClassName } =
            this.props;

        if (multiple) {
            const { selectedAll } = this.state;
            const commonProps = {
                item,
                checkboxClassName,
                onClick: this.handleSelectableClick,
            };

            if (this.isItemSelectAll(item)) {
                return {
                    ...commonProps,
                    id: item.value,
                    children: selectAllText,
                    selected: selectedAll,
                    className: selectAllContentClassName,
                    onClick: this.handleSelectableClick,
                };
            }

            return {
                ...commonProps,
            };
        }

        return {};
    };

    isAllSelected = (selectedItems) => {
        const validItems = this.getAllItems({ onlyValid: true });

        const unselectedItems = validItems.filter((item) => !selectedItems.includes(item));
        return unselectedItems.length === 0;
    };

    isItemSelected = (item) => {
        const { selectedValues } = this.state;

        return selectedValues.includes(item.value);
    };

    isItemSelectAll = (item) => item && item === this.selectAllRef.current;

    resetSelection() {
        this.setState({
            selectedValues: [],
            selectedAll: false,
        });
    }

    // eslint-disable-next-line react/no-unused-class-component-methods
    toggleItemInSelection(selectedItem) {
        const selectedItems = this.getSelectedItems();

        const itemIndex = selectedItems.indexOf(selectedItem);

        if (itemIndex >= 0) {
            return selectedItems.filter((item) => item !== selectedItem);
        }

        return [...selectedItems, selectedItem];
    }

    renderItemsContainer = ({ children }) => {
        const { selectedValues, selectedAll } = this.state;
        const selectAllValue = this.selectAllRef.current?.value;

        return (
            <Selectable
                propagateClicksOnInput
                selectedItems={selectedAll ? [selectAllValue, ...selectedValues] : selectedValues}
            >
                {children}
            </Selectable>
        );
    };

    renderItem = ({ item }) => {
        const { selectAllText, renderItem } = this.props;

        if (this.isItemSelectAll(item)) {
            return selectAllText;
        }

        return renderItem({ item });
    };

    renderToggle = () => {
        const {
            placeholder,
            tabIndex,
            disabled,
            selectedItemsText,
            renderSelection,
            renderToggle,
            toggleClassName,
        } = this.props;
        const { isOpen } = this.state;
        const { id, highlightedItem } = this.state;
        const selectedItems = this.getSelectedItems();

        // we want to set aria-activedescendant only when exactly 1 item is highlighted/selected
        let itemIndex = null;
        if (highlightedItem) {
            itemIndex = this.getIndexOfItem(highlightedItem);
        } else if (selectedItems.length === 1) {
            itemIndex = this.getIndexOfItem(selectedItems[0]);
        }

        const idItem = isOpen && itemIndex !== null ? `${id}-item-${itemIndex}` : undefined;

        const props = {
            id,
            placeholder,
            tabIndex,
            disabled,
            'aria-activedescendant': idItem,
            ...omitProps(this.props, SelectContainer, MenuDropdown),
            className: toggleClassName,
        };

        const selection = this.getSelection();
        const toggle = renderProp(
            renderToggle,
            <SelectToggle
                selection={selection}
                renderSelection={renderSelection}
                selectedItemsText={selectedItemsText}
            />,
            { selection },
        );

        return cloneElement(toggle, props);
    };

    render() {
        const {
            dropdownRef,
            toggleRef,
            isOpen,
            disabled,
            separated,
            fullWidth,
            usePortal = true,
            emptyLabel,
            multiple,
            isLoading,
            loadingText,
            onClick,
            onFocus,
            openOnFocus,
            openOnKeyDown,
            selectWithSpace,
            selectWithArrow,
            toggleOnClick,
            toggleWithSpace,
            toggleWithEnter,
            highlightWithArrow,
            ignoreNavigationKeys,
            closeWithTab,
            onOpen,
            onClose,
            menuClassName,
            dropdownClassName,
            containerClassName,
            className,
            ...rest
        } = this.props;
        const { id, highlightedItem } = this.state;
        const { RIGHT, TOGGLE } = MenuDropdown.ALIGNMENT;

        const itemsToRender = this.getItemsToRender();
        const menuClassNames = cx(
            'dropdown-menu',
            styles.menu,
            { 'dropdown-menu-right': !fullWidth },
            { 'g-dropdown-separator': separated },
            menuClassName,
        );

        const items = this.isSelectAllAvailable()
            ? [this.selectAllRef.current, ...itemsToRender]
            : itemsToRender;

        return (
            <MenuDropdown
                {...pickProps(omitProps(rest, SelectContainer), MenuDropdown)}
                useFocusTrap={false}
                id={id}
                items={items}
                alignment={fullWidth ? TOGGLE : RIGHT}
                arrow={false}
                disabled={disabled}
                usePortal={usePortal}
                emptyLabel={emptyLabel}
                highlightedItem={highlightedItem} // TODO:
                popoverRef={this.popoverRef}
                dropdownRef={mergeRefs(this.dropdownRef, dropdownRef)}
                toggleRef={mergeRefs(this.toggleRef, toggleRef)}
                renderToggle={this.renderToggle}
                isLoading={isLoading}
                loadingText={loadingText}
                isItemSelected={this.isItemSelected}
                itemComponent={multiple ? MultiSelectableItem : undefined}
                itemComponentProps={this.getItemProps}
                isMenu={false}
                renderItem={this.renderItem}
                renderItemsContainer={this.renderItemsContainer}
                openOnFocus={openOnFocus}
                openOnKeyDown={openOnKeyDown}
                selectWithSpace={selectWithSpace} // TODO:
                toggleOnClick={toggleOnClick}
                toggleWithSpace={multiple ? false : toggleWithSpace}
                toggleWithEnter={multiple ? false : toggleWithEnter} // TODO:
                highlightWithArrow={highlightWithArrow}
                ignoreNavigationKeys={ignoreNavigationKeys}
                openWithArrow={!selectWithArrow}
                closeWithTab={closeWithTab}
                closeOnItemClick={false}
                closeOnClickOutside={false}
                isOpen={isOpen}
                className={className}
                menuClassName={menuClassNames}
                dropdownClassName={dropdownClassName}
                containerClassName={containerClassName}
                onClickOutside={this.handleClickOutside}
                onClick={onClick}
                onFocus={onFocus}
                onKeyDown={this.handleKeyDown}
                onHighlight={this.highlightItem}
                onBeforeOpen={this.handleBeforeOpen}
                onOpen={onOpen}
                onBeforeClose={this.handleBeforeClose}
                onClose={onClose}
                onSelect={this.selectItem}
            />
        );
    }
}
